import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'

const API_BASE = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000'

export type WaitlistReason = 'urgent_project' | 'questions' | 'status_check' | 'other'

interface WaitlistInquiry {
  id: string
  user_id: string
  reason: WaitlistReason
  message: string | null
  created_at: string
  reviewed_at: string | null
  reviewed_by: string | null
}

interface SubmitInquiryParams {
  reason: WaitlistReason
  message?: string
}

/**
 * Hook to query the current user's waitlist inquiries
 */
export function useWaitlistInquiries() {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['waitlist-inquiries', user?.id],
    queryFn: async () => {
      if (!user?.id) return []

      const { data, error } = await supabase
        .from('waitlist_inquiries')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })

      if (error) throw error
      return data as WaitlistInquiry[]
    },
    enabled: !!user?.id,
  })
}

/**
 * Hook to submit a waitlist inquiry via backend API
 * Sends email notification to admin
 */
export function useSubmitWaitlistInquiry() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ reason, message }: SubmitInquiryParams) => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) {
        throw new Error('Not authenticated')
      }

      const response = await fetch(`${API_BASE}/api/waitlist/inquiry`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reason, message }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || 'Failed to submit inquiry')
      }

      return await response.json()
    },
    onSuccess: () => {
      // Invalidate inquiries query to refetch
      queryClient.invalidateQueries({ queryKey: ['waitlist-inquiries'] })
    },
  })
}
